import { ColumnInterface } from "components/tables/interfaces";
import Table from "components/tables/table";
import { renderListSchemaColumn } from "lib/schema";
import { ListEntity } from "models/list-entity";
import { Fragment, FunctionComponent, useEffect, useState } from "react";
import { adminDoGetListEntities } from "services/admin/list-entity";
import { useLocale } from "stores/hooks/locale";
import ListSchemaAddColumnModal from "./list-schema-add-column-modal";
import { useTranslate } from "stores/hooks/translation";
import { SchemaColumn } from "models/generic";
import { List } from "models/list";

interface ListSchemaTableProps {
  list: List;
  schema: SchemaColumn[];
  onUpdate: (schema: SchemaColumn[]) => Promise<void>;
}

interface Row {
  order: number;
  column: SchemaColumn;
}

const ListSchemaTable: FunctionComponent<ListSchemaTableProps> = ({
  list,
  schema,
  onUpdate,
}) => {
  const locale = useLocale();
  const translate = useTranslate();
  const [entities, toggleEntities] = useState<ListEntity[]>([]);
  const [addModal, toggleAddModal] = useState(false);

  const update = async () => {
    const entities = await adminDoGetListEntities(list);
    toggleEntities(entities);
  };

  useEffect(() => {
    update();
  }, []);

  const rows: Row[] = schema.map((c, index) => ({
    order: index,
    column: c,
  }));

  const columns: ColumnInterface[] = [
    {
      key: "column",
      header: "Sarake",
      render: (row: Row) =>
        renderListSchemaColumn(row.column, entities, locale, translate),
    },
    {
      key: "width",
      header: "Koko",
      render: (row: Row) => {
        if (row.column.width) {
          return `${row.column.width}px`;
        }
        return translate("column_width_automatic");
      },
    },
  ];

  const handleAdd = async () => toggleAddModal(true);

  const handleDelete = async (row: Row) => {
    const newSchema = schema.filter((c) => c !== row.column);
    onUpdate(newSchema);
  };

  const handleUp = async (row: Row) => {
    const index = schema.indexOf(row.column);
    const temp = schema[index - 1];
    schema[index - 1] = row.column;
    schema[index] = temp;
    onUpdate(schema);
  };

  const handleDown = async (row: Row) => {
    const index = schema.indexOf(row.column);
    const temp = schema[index + 1];
    schema[index + 1] = row.column;
    schema[index] = temp;
    onUpdate(schema);
  };

  return (
    <Fragment>
      <Table
        name={`list-${list.uuid}-list-schema`}
        index
        columns={columns}
        data={rows}
        adder={{
          text: "Lisää sarake",
          onClick: handleAdd,
        }}
        mover={{
          onUp: handleUp,
          onDown: handleDown,
          isUpDisabled: (row: Row) => schema.indexOf(row.column) === 0,
          isDownDisabled: (row: Row) =>
            schema.indexOf(row.column) === schema.length - 1,
        }}
        deleter={{
          onClick: handleDelete,
          isDisabled: () => schema.length === 1,
        }}
      />
      {addModal && (
        <ListSchemaAddColumnModal
          entities={entities}
          onUpdate={async (column: SchemaColumn) => {
            onUpdate([...schema, column]);
          }}
          onClose={async () => toggleAddModal(false)}
        />
      )}
    </Fragment>
  );
};

export default ListSchemaTable;
